import { useState, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
// Label component - simple implementation
function Label({
  htmlFor,
  children,
  className = '',
}: {
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <label htmlFor={htmlFor} className={`text-sm font-medium text-text ${className}`}>
      {children}
    </label>
  )
}
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'

interface ExternalMappings {
  vrbo_property_id?: string | null
  airbnb_property_id?: string | null
  external_id?: string | null
  uplisting_id?: string | null
  uplisting_property_id?: string | null
}

interface PropertyForModal {
  id?: string
  property_id?: string
  external_mappings?: ExternalMappings | null
}

interface SavedMappings {
  vrbo_property_id: string | null
  airbnb_property_id: string | null
  external_id: string | null
  uplisting_id: string | null
}

interface PropertyExternalIdsModalProps {
  property: PropertyForModal | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (result: { propertyId: string; mappings: SavedMappings }) => void
}

export function PropertyExternalIdsModal({
  property,
  open,
  onOpenChange,
  onSaved,
}: PropertyExternalIdsModalProps) {
  const { toast } = useToast()
  const [isSaving, setIsSaving] = useState(false)
  const propertyId = property?.id || property?.property_id
  const externalMappings = property?.external_mappings || {}

  // Initialize form data from property prop (uses same payload structure as properties list)
  const [formData, setFormData] = useState({
    vrbo_property_id: externalMappings.vrbo_property_id || '',
    airbnb_property_id: externalMappings.airbnb_property_id || '',
    external_id: externalMappings.external_id || '',
    uplisting_id: externalMappings.uplisting_id || externalMappings.uplisting_property_id || '',
  })

  const handleSave = async () => {
    if (!propertyId) return

    setIsSaving(true)
    try {
      // Use PUT /properties/:id with external_mappings payload (same structure as GET)
      await api.updateProperty(propertyId, {
        external_mappings: {
          vrbo_property_id: formData.vrbo_property_id || null,
          airbnb_property_id: formData.airbnb_property_id || null,
          external_id: formData.external_id || null,
          uplisting_id: formData.uplisting_id || null,
        },
      })
      onSaved?.({
        propertyId,
        mappings: {
          vrbo_property_id: formData.vrbo_property_id || null,
          airbnb_property_id: formData.airbnb_property_id || null,
          external_id: formData.external_id || null,
          uplisting_id: formData.uplisting_id || null,
        },
      })
      toast({
        title: 'External IDs updated',
        description: 'Property external ID mappings have been saved successfully.',
      })
      onOpenChange(false)
    } catch (error: unknown) {
      toast({
        title: 'Failed to update external IDs',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Property External IDs</DialogTitle>
          <DialogDescription>
            Manage external ID mappings for this property. These IDs are used to match emails to
            properties.
          </DialogDescription>
        </DialogHeader>

        {
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="vrbo_property_id">VRBO Property ID</Label>
              <Input
                id="vrbo_property_id"
                value={formData.vrbo_property_id}
                onChange={e => setFormData({ ...formData, vrbo_property_id: e.target.value })}
                placeholder="e.g., 3576061"
              />
              <p className="text-xs text-textMuted">
                The VRBO property identifier (found in VRBO email subjects: Vrbo #3576061)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="airbnb_property_id">Airbnb Property ID</Label>
              <Input
                id="airbnb_property_id"
                value={formData.airbnb_property_id}
                onChange={e => setFormData({ ...formData, airbnb_property_id: e.target.value })}
                placeholder="e.g., 594119710195082545"
              />
              <p className="text-xs text-textMuted">
                The Airbnb property identifier (found in Airbnb email URLs:
                /rooms/594119710195082545)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="external_id">External ID</Label>
              <Input
                id="external_id"
                value={formData.external_id}
                onChange={e => setFormData({ ...formData, external_id: e.target.value })}
                placeholder="e.g., 25064"
              />
              <p className="text-xs text-textMuted">
                External identifier from VRBO/Uplisting (may not be needed for matching)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="uplisting_id">Uplisting Property ID</Label>
              <Input
                id="uplisting_id"
                value={formData.uplisting_id}
                onChange={e => setFormData({ ...formData, uplisting_id: e.target.value })}
                placeholder="e.g., 116799"
              />
              <p className="text-xs text-textMuted">
                The Uplisting property identifier (used for syncing reservations)
              </p>
            </div>
          </div>
        }

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
